import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CraftedBy from "../../components/CraftedBy";
import { api } from "../../lib/api";
import { HC } from "../hr/theme";
import {
  LayoutDashboard, BellRing, ListChecks, AlertOctagon, Wrench, MessageSquareWarning,
  FileBarChart2, Sparkles, Settings as SettingsIcon, Menu as MenuIcon, ChevronDown,
  ArrowLeft, LogOut, Crown, MapPin, Headphones,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const NAV = [
  { path: "", label: "Dashboard", Icon: LayoutDashboard },
  { path: "alerts", label: "Alerts", Icon: BellRing },
  { path: "tasks", label: "Tasks & Checklists", Icon: ListChecks },
  { path: "escalations", label: "Escalations", Icon: AlertOctagon },
  { path: "maintenance", label: "Maintenance & Renewals", Icon: Wrench },
  { path: "complaints", label: "Complaints", Icon: MessageSquareWarning },
  { path: "reports", label: "Reports", Icon: FileBarChart2 },
  { path: "intelligence", label: "AI Intelligence", Icon: Sparkles },
  { path: "settings", label: "Settings", Icon: SettingsIcon },
];

const META = {
  "": { title: null, sub: "What is happening, what needs attention, and what to do about it." },
  alerts: { title: "Operational Alert Centre", sub: "Every exception across inventory, procurement, finance, HR and operations." },
  tasks: { title: "Tasks & Checklists", sub: "Assign work, run recurring checklists, track completion." },
  escalations: { title: "Escalations", sub: "Missed tasks and repeated-miss patterns needing a decision." },
  maintenance: { title: "Maintenance & Renewals", sub: "Equipment issues, service schedules and contract renewals." },
  complaints: { title: "Complaints", sub: "Customer and staff complaints with resolution tracking." },
  reports: { title: "Enterprise Reporting", sub: "Revenue, COGS, workforce and procurement — with exports." },
  intelligence: { title: "AI Management Intelligence", sub: "Advisory only. Generated from a deterministic payload and fully audited." },
  settings: { title: "Management Settings", sub: "Escalation chain, repeat-miss rules, alert sources and AI toggle." },
};

export const PERIODS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"],
  ["last_7", "Last 7 Days"], ["this_month", "This Month"], ["last_month", "Last Month"],
  ["last_30", "Last 30 Days"], ["ytd", "Year to Date"],
];

export default function ManagementLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selLoc, setSelLoc] = useState("all");
  const [period, setPeriod] = useState("this_week");
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);

  const scoped = !!user?.location_id && !user?.is_admin;
  const locationId = scoped ? user.location_id : selLoc === "all" ? null : selLoc;

  useEffect(() => {
    api.get("/management/alerts", { params: { location_id: locationId || undefined, limit: 1 } })
      .then((d) => setAlertCount(d?.total || 0)).catch(() => {});
  }, [locationId]);

  const path = loc.pathname.replace(/^\/management\/?/, "");
  const meta = META[path] || META[""];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const first = (user?.name || "there").split(" ")[0];
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold transition-colors ${isActive ? "" : "hover:bg-[#FBF8F2]"}`;
  const navStyle = ({ isActive }) => ({
    background: isActive ? HC.burgundySoft : "transparent",
    color: isActive ? HC.burgundy : HC.text,
    borderLeft: isActive ? `3px solid ${HC.gold}` : "3px solid transparent",
  });

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: HC.canvas }}>
      <aside data-testid="mgmt-sidebar"
        className={`w-[252px] ${mobileOpen ? "translate-x-0" : "-translate-x-full invisible"} lg:translate-x-0 lg:visible fixed lg:static z-40 h-full flex flex-col transition-transform duration-300`}
        style={{ background: HC.panel, borderRight: `1px solid ${HC.border}` }}>
        <div className="h-[84px] flex items-center gap-3 px-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: HC.burgundy }}>
            <Crown size={21} color={HC.gold} strokeWidth={2.2} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-brand font-bold text-[26px] tracking-tight" style={{ color: HC.burgundy }}>Sir Monty</div>
            <div className="text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.soft }}>ERP</div>
          </div>
        </div>

        <div className="px-5 pt-2 pb-3 text-[10px] font-bold tracking-[0.2em]" style={{ color: HC.gold }}>MANAGEMENT</div>

        <nav className="flex-1 overflow-y-auto px-3 space-y-1">
          {NAV.map((item) => (
            <NavLink key={item.path} to={item.path === "" ? "/management" : `/management/${item.path}`}
              end={item.path === ""} onClick={() => setMobileOpen(false)}
              data-testid={`mgmt-nav-${item.path || "dashboard"}`} className={navClass} style={navStyle}>
              <item.Icon size={18} className="shrink-0" />
              <span className="truncate">{item.label}</span>
              {item.path === "alerts" && alertCount > 0 && (
                <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: HC.redTint, color: HC.red }} data-testid="mgmt-nav-alert-count">{alertCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mx-4 my-4 rounded-2xl border flex items-center gap-3 px-4 h-14"
          style={{ borderColor: HC.border, background: HC.panelSoft }}>
          <Headphones size={17} style={{ color: HC.burgundy }} />
          <span className="text-sm font-semibold" style={{ color: HC.text }}>Need Help?</span>
        </div>

        <div className="px-5 pb-4 flex items-center gap-3">
          <Crown size={22} style={{ color: HC.gold }} />
          <div className="font-brand italic leading-tight" style={{ color: HC.burgundy }}>
            <div className="text-[13px] font-semibold">Great Food,</div>
            <div className="text-[13px] font-semibold">Great Moments.</div>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="shrink-0 h-[84px] flex items-center gap-3 px-4 lg:px-7 border-b"
          style={{ borderColor: HC.border, background: HC.canvas }}>
          <button className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center"
            onClick={() => setMobileOpen(true)} style={{ color: HC.text }} data-testid="mgmt-mobile-menu">
            <MenuIcon size={20} />
          </button>
          <div className="min-w-0">
            <div className="font-brand font-bold text-[22px] lg:text-[26px] leading-tight truncate"
              style={{ color: HC.text }} data-testid="mgmt-page-title">
              {meta.title || `${greeting}, ${first}`}
            </div>
            <div className="text-[12px] lg:text-[13px] truncate" style={{ color: HC.soft }}>{meta.sub}</div>
          </div>

          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-2 h-11 px-3.5 rounded-xl border bg-white" style={{ borderColor: HC.border }}>
            <MapPin size={15} style={{ color: HC.muted }} />
            {scoped ? (
              <span className="text-sm font-semibold" style={{ color: HC.text }}>
                {locations.find((l) => l.id === user.location_id)?.name || "My location"}
              </span>
            ) : (
              <select data-testid="mgmt-location-filter" value={selLoc} onChange={(e) => setSelLoc(e.target.value)}
                className="text-sm font-semibold bg-transparent outline-none pr-1" style={{ color: HC.text }}>
                <option value="all">All Locations</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-2 h-11 px-3.5 rounded-xl border bg-white" style={{ borderColor: HC.border }}>
            <select data-testid="mgmt-period-filter" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="text-sm font-semibold bg-transparent outline-none" style={{ color: HC.text }}>
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5" data-testid="mgmt-user-menu">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: HC.borderSoft, color: HC.muted }}>{initials}</div>
                <ChevronDown size={14} style={{ color: HC.soft }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs font-normal" style={{ color: HC.muted }}>{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/")} data-testid="mgmt-back-to-erp">
                <ArrowLeft size={15} className="mr-2" /> Back to ERP
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} className="text-[#A52222]">
                <LogOut size={15} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="mgmt-main">
          <div className="p-4 lg:p-6 max-w-[1680px] mx-auto w-full">
            <Outlet context={{ locationId, locations, period, setPeriod, scoped }} />
          </div>
          <footer className="px-4 lg:px-8 pb-4 pt-1 flex items-center justify-between text-[11px]" style={{ color: "#A58F7C" }}>
            <div>© {new Date().getFullYear()} Sir Monty Enterprise ERP · Management</div>
            <CraftedBy />
          </footer>
        </main>
      </div>
    </div>
  );
}
